import { useEffect, useMemo, useState } from 'react';
import type { Editor } from '../editor';
import { runRules, type RuleReport, type Violation } from '../rules/engine';
import { DEFAULT_RULE_SET, STANDARD_RULES } from '../rules/standardRules';
import { buildCatalogMap } from '../blocks/catalog';
import { FALLBACK_CATALOG } from '../blocks/standardLibrary';

/**
 * 검사 항목 켜고 끄기 — 개인 설정.
 *
 * 도면마다 다르게 저장하지 않는다. "이 사람은 항상 소방 규칙을 꺼 둔다" 처럼
 * 검사자 개인의 습관이지, 도면의 속성이 아니기 때문이다 — 로그인 계정별이
 * 아니라 이 브라우저를 쓰는 사람 기준(localStorage)으로 충분하다.
 */
const DISABLED_RULES_KEY = 'storecad.rulePanel.disabledRules';

function loadDisabledRules(): string[] {
  try {
    const raw = localStorage.getItem(DISABLED_RULES_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function saveDisabledRules(ids: string[]): void {
  try {
    localStorage.setItem(DISABLED_RULES_KEY, JSON.stringify(ids));
  } catch {
    // 저장 실패해도 검사 자체는 계속 동작해야 한다 — 조용히 무시
  }
}

/**
 * 검증 패널 (작업지시서 P5-02 / P5-03 / P5-04)
 *
 * ── 화면이 반드시 구분해서 보여줘야 하는 것 ─────────────────────────────
 *
 *   "위반 0건" 이 **다 통과했다**는 뜻인지, **검사를 못 했다**는 뜻인지.
 *   둘을 뭉개면 사용자는 검사가 죽은 줄도 모르고 안심한다.
 *   → 검사한 규칙 수와 건너뛴 규칙을 같이 표시한다.
 *
 * ── 성능 ────────────────────────────────────────────────────────────────
 *
 *   규칙 검사는 O(n²) 다. 편집할 때마다 돌리면 큰 도면에서 화면이 멎는다.
 *   → 자동 검사는 디바운스하고, 객체가 많으면 수동으로만 돌린다.
 */

const CATALOG = buildCatalogMap(FALLBACK_CATALOG);

/** 이 이상이면 자동 검사를 끈다. O(n²) 이 체감되는 지점. */
const AUTO_LIMIT = 800;

export function RulePanel({ editor }: { editor: Editor }) {
  const [report, setReport] = useState<RuleReport | null>(null);
  const [running, setRunning] = useState(false);
  const [auto, setAuto] = useState(true);
  const [tick, setTick] = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [disabledRuleIds, setDisabledRuleIds] = useState<string[]>(() => loadDisabledRules());

  useEffect(() => editor.scene.subscribe(() => setTick((t) => t + 1)), [editor]);

  const count = editor.scene.count;
  const tooBig = count > AUTO_LIMIT;

  const ruleSet = useMemo(
    () => ({ ...DEFAULT_RULE_SET, disabled: disabledRuleIds }),
    [disabledRuleIds]
  );

  const run = useMemo(
    () => () => {
      setRunning(true);
      // 다음 프레임에 돌린다 — 버튼이 눌린 티가 나야 한다
      window.setTimeout(() => {
        const r = runRules(
          STANDARD_RULES,
          {
            doc: editor.scene.doc,
            blocks: editor.scene.blocks,
            clearanceOf: (code) => CATALOG.get(code)?.clearance,
          },
          ruleSet
        );
        setReport(r);
        setRunning(false);
      }, 0);
    },
    [editor, ruleSet]
  );

  // 자동 검사 — 편집이 멎고 800ms 뒤. 검사 항목을 켜고 끄면 즉시 다시 돈다.
  useEffect(() => {
    if (!auto || tooBig) return;
    const t = window.setTimeout(run, 800);
    return () => window.clearTimeout(t);
  }, [auto, tooBig, tick, run]);

  function toggleRule(ruleId: string, on: boolean) {
    const next = on ? disabledRuleIds.filter((id) => id !== ruleId) : [...disabledRuleIds, ruleId];
    setDisabledRuleIds(next);
    saveDisabledRules(next);
  }

  return (
    <div className="rules">
      <div className="rules__head">
        <button type="button" className="btn btn--primary btn--sm" disabled={running} onClick={run}>
          {running ? '검사 중…' : '지금 검사'}
        </button>
        <label className="rules__auto">
          <input
            type="checkbox"
            checked={auto && !tooBig}
            disabled={tooBig}
            onChange={(e) => setAuto(e.target.checked)}
          />
          자동 검사
        </label>
        <button
          type="button"
          className="btn btn--sm"
          aria-expanded={showSettings}
          onClick={() => setShowSettings((s) => !s)}
        >
          검사 항목 {showSettings ? '숨기기' : '설정'}
        </button>
      </div>

      {showSettings && (
        <ul className="rules__settings" aria-label="검사 항목 켜고 끄기">
          {STANDARD_RULES.map((rule) => {
            const on = !disabledRuleIds.includes(rule.id);
            return (
              <li key={rule.id} className="rules__settings-item">
                <label>
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={(e) => toggleRule(rule.id, e.target.checked)}
                  />
                  <span className="rules__settings-name">
                    {rule.name}
                    <span className={`rules__stat rules__stat--${rule.severity === 'info' ? 'muted' : rule.severity}`}>
                      {rule.severity === 'error' ? '오류' : rule.severity === 'warning' ? '경고' : '안내'}
                    </span>
                  </span>
                </label>
                <p className="rules__settings-desc">{rule.rationale}</p>
              </li>
            );
          })}
        </ul>
      )}

      {tooBig && (
        <p className="cad-panel__hint">
          객체가 {count.toLocaleString('ko-KR')}개라 자동 검사를 껐습니다. 편집이 느려지기
          때문입니다. 필요할 때 직접 돌리세요.
        </p>
      )}

      {!report && <p className="cad-panel__hint">아직 검사하지 않았습니다. "검사 항목 설정"에서 무엇을 검사하는지 볼 수 있습니다.</p>}

      {report && <Summary report={report} />}

      {report && (
        <ul className="rules__list">
          {report.violations.map((v, i) => (
            <li key={`${v.ruleId}-${i}`} className={`rule-item rule-item--${v.severity}`}>
              <button
                type="button"
                className="rule-item__main"
                onClick={() => {
                  // 위반 지점으로 데려간다 — 목록만 보여주면 찾느라 시간이 다 간다
                  editor.selection.clear();
                  for (const id of v.entityIds) {
                    if (editor.scene.get(id)) editor.selection.add(id);
                  }
                  if (v.box) {
                    editor.centerOn({
                      x: Math.round((v.box.minX + v.box.maxX) / 2),
                      y: Math.round((v.box.minY + v.box.maxY) / 2),
                    });
                  } else {
                    editor.zoomSelection();
                  }
                }}
              >
                <span className="rule-item__icon" aria-hidden="true">
                  {v.severity === 'error' ? '✕' : v.severity === 'warning' ? '!' : 'i'}
                </span>
                <span className="rule-item__msg">{v.message}</span>
              </button>

              <button
                type="button"
                className="rule-item__why"
                aria-expanded={expanded === `${v.ruleId}-${i}`}
                onClick={() =>
                  setExpanded(expanded === `${v.ruleId}-${i}` ? null : `${v.ruleId}-${i}`)
                }
              >
                왜?
              </button>

              {expanded === `${v.ruleId}-${i}` && (
                <p className="rule-item__rationale">{rationaleOf(v)}</p>
              )}
            </li>
          ))}
        </ul>
      )}

      {report && report.violations.length === 0 && (
        <p className="rules__clean">
          규칙 {report.checkedRules}개를 검사했고 위반이 없습니다.
        </p>
      )}

      {report && report.skipped.length > 0 && (
        <details className="rules__skipped">
          <summary>검사하지 않은 규칙 {report.skipped.length}개</summary>
          <ul>
            {report.skipped.map((s) => (
              <li key={s.ruleId}>
                <code>{s.ruleId}</code> — {s.reason}
              </li>
            ))}
          </ul>
        </details>
      )}

      {/* 오해를 막는다 — 여기서 통과했다고 인허가가 통과하는 게 아니다 */}
      <p className="cad-panel__hint rules__disclaimer">
        도면상 기하 조건만 검사합니다. 실제 소방·건축 인허가는 별도 검토를 받아야 합니다.
      </p>
    </div>
  );
}

function Summary({ report }: { report: RuleReport }) {
  return (
    <div className="rules__summary">
      <span className="rules__stat rules__stat--error">
        <span aria-hidden="true">✕</span> 오류 {report.errorCount}
      </span>
      <span className="rules__stat rules__stat--warning">
        <span aria-hidden="true">!</span> 경고 {report.warningCount}
      </span>
      <span className="rules__stat rules__stat--muted">규칙 {report.checkedRules}개 검사</span>
    </div>
  );
}

function rationaleOf(v: Violation): string {
  return STANDARD_RULES.find((r) => r.id === v.ruleId)?.rationale ?? '설명이 없습니다.';
}
